import React, { FC, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ExpandMoreIcon } from 'application/assets';
import useOnClickOutside from 'application/hooks/useOnClickOutside';
import Input from '../Input';
import Checkbox from '../Checkbox';
import { DropdownProps, Option } from './models';
import {
  DropdownStyled,
  OptionCheckBox,
  OptionItem,
  OptionsList,
} from './styles';

/**
 * Functional component for a dropdown menu.
 *
 * @param id - The ID of the dropdown.
 * @param label - The label to display above the dropdown.
 * @param placeholder - The placeholder text to display when no option is selected.
 * @param searchPlaceholder - The placeholder text to display in the search input.
 * @param value - The current value(s) of the dropdown.
 * @param onChange - A function to call when the user selects an option.
 * @param hint - An optional hint message to display below the dropdown.
 * @param disabled - Whether the dropdown is disabled or not.
 * @param options - An array of options to display in the dropdown.
 * @param isMulti - Whether multiple selections are allowed.
 * @param errorMessage - An optional error message to display below the dropdown.
 * @param withSearch - Whether to include a search input in the dropdown.
 * @param LeftIcon - An optional icon to display on the left side of the dropdown.
 */
const Dropdown: FC<DropdownProps> = ({
  id,
  label,
  placeholder,
  searchPlaceholder,
  value,
  onChange,
  hint,
  disabled,
  options,
  isMulti,
  errorMessage,
  withSearch,
  LeftIcon,
}) => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const [isActive, setIsActive] = useState(false);
  const [searchItem, setSearchItem] = useState('');

  useOnClickOutside(ref, () => setIsActive(false));

  // Function to filter options based on search input
  const optionList = useMemo(() => {
    if (!searchItem) return options;
    return options.filter((item) =>
      item.label.toLocaleLowerCase().includes(searchItem.toLocaleLowerCase()),
    );
  }, [options, searchItem]);

  // Function to handle option selection for multiple selections
  const handleChange = (selectedItem: string) => {
    if (!Array.isArray(value)) return;
    const valueAlreadyAdded = value.some((item) => item === selectedItem);
    if (valueAlreadyAdded) {
      const filtredValue = value.filter((item) => item !== selectedItem);
      onChange(filtredValue);
    } else {
      onChange([...value, selectedItem]);
    }
    return;
  };

  // Function to check if a checkbox is checked for multiple selections
  const IsValueChecked = (item: Option) => {
    const isValueArray = Array.isArray(value);
    return isValueArray && value.some((i) => i === item.value);
  };

  // Function to get the current dropdown value based on selection type
  const getValue = () => {
    if (isMulti) return `${value.length} ${t('global.selected')}`;
    const currentOption = optionList.find(
      (item) => String(item.value) === String(value),
    );
    return currentOption?.label;
  };

  const isListNotEmpty = isActive && !!options.length;

  return (
    <DropdownStyled
      isActive={isActive}
      disabled={disabled}
      ref={ref}
      isError={!!errorMessage}
      className="dropdown"
    >
      {label && <label htmlFor={id}>{label}</label>}
      <section onMouseDown={() => !disabled && setIsActive(!isActive)}>
        {LeftIcon ? (
          <div>
            <LeftIcon />
            {!value?.length ? (
              <span>{placeholder}</span>
            ) : (
              <h2>{getValue()}</h2>
            )}
          </div>
        ) : (
          <>
            {!value?.length ? (
              <span>{placeholder}</span>
            ) : (
              <h2>{getValue()}</h2>
            )}
          </>
        )}
        <ExpandMoreIcon />
      </section>
      {isListNotEmpty && !isMulti ? (
        <OptionsList className="optionList">
          {withSearch && (
            <Input
              id={'search'}
              type="search"
              value={searchItem}
              onChange={(e) => setSearchItem(e.target?.value || '')}
              placeholder={searchPlaceholder}
              onClear={() => setSearchItem('')}
            />
          )}
          {optionList.map((item) => (
            <OptionItem
              key={item.value}
              onClick={() => {
                onChange(item);
                setIsActive(false);
              }}
              selected={value === String(item.value)}
            >
              {item.label}
            </OptionItem>
          ))}
        </OptionsList>
      ) : (
        ''
      )}
      {isListNotEmpty && isMulti ? (
        <OptionsList className="optionListMulti">
          {withSearch && (
            <Input
              id={'search'}
              type="search"
              value={searchItem}
              onChange={(e) => setSearchItem(e.target?.value || '')}
              placeholder={searchPlaceholder}
              onClear={() => setSearchItem('')}
              autoComplete="off"
            />
          )}
          {optionList.map((item, index) => (
            <OptionCheckBox
              key={`${item.value}_${index}`}
              htmlFor={`${item.label}_${index}`}
              selected={IsValueChecked(item)}
            >
              <Checkbox
                id={`${item.label}_${index}`}
                checked={IsValueChecked(item)}
                onChange={() => handleChange(String(item.value))}
              />
              {item.label}
            </OptionCheckBox>
          ))}
        </OptionsList>
      ) : (
        ''
      )}
      {(hint || errorMessage) && (
        <span>{errorMessage ? errorMessage : hint}</span>
      )}
    </DropdownStyled>
  );
};

export default Dropdown;
